import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { config } from "configurations/config";
import { environment } from "environments/environment";
import { Subscription } from "rxjs";
import {
  CeloFileUploader,
  UploadFailure,
  UploadItem,
  UploadRequest,
  UploadSuccess,
} from "../../shared/file-upload";
import { AlertService } from "../old/alert.service";
import { SharedService } from "../old/shared.service";
import { AuthService } from "../services/auth.service";
import {
  SUPPORTED_VIDEO_FORMATS,
  VIDEO_PREPARER,
  VideoTooLargeError,
  VideoTooLongError,
} from "../services/video-import";

/** @deprecated Use UploadItem from shared/file-upload instead. */
export type UploadServiceFileItem = UploadItem;

/** Conversion state of a queued video, kept outside the upload item. */
export interface VideoUploadState {
  isConverting: boolean;
  /** First-frame JPEG sent with the upload. */
  thumbnail?: File;
  /** Object URL of the thumbnail, for the preview. */
  thumbnailUrl?: string;
  /** Object URL of the converted video, for the preview. */
  videoUrl?: string;
}

/** @deprecated */
@Injectable({
  providedIn: "root",
})
export class FileUploadService {
  errored = false;
  type = "Photos";
  targetConversationId = "";
  allowPatient = false;
  limit = 1;
  patient: any = {
    uid: "",
    firstName: "",
    lastName: "",
    dateOfBirth: null,
    gender: null,
  };

  uploadedFiles: Array<any> = [];
  payload: any;
  public uploader: CeloFileUploader;
  uploaderQueueItems: { fileItem: UploadItem; description: string }[] = [];
  supportedDocumentFormats = ["application/pdf"];
  supportedImageFormats = ["image/jpg", "image/jpeg", "image/png"];
  // This will almost always not match anything as browsers treat mp4 files are video/mp4 by default
  supportedAudioFormats = ["audio/mp4"];
  supportedVideoFormats = SUPPORTED_VIDEO_FORMATS;

  private subscriptions = new Subscription();

  /** Keyed by upload item id. */
  private videoStates = new Map<string, VideoUploadState>();
  /** Videos still being converted. `upload()` waits for these. */
  private videoConversions = new Map<string, Promise<void>>();

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private sharedService: SharedService,
    private alertService: AlertService
  ) {}

  private prepareVideo = inject(VIDEO_PREPARER);

  initialize(configData, callback) {
    this.uploadedFiles = [];
    this.errored = false;
    const data = configData;
    this.type = data.type;
    this.targetConversationId = data.conversationId;
    this.allowPatient = data.allowPatient ? data.allowPatient : false;
    this.limit = data.queueLimit ? data.queueLimit : 1;
    this.payload = data.payload;

    this.subscriptions.unsubscribe();
    this.subscriptions = new Subscription();
    this.clearVideoStates();
    this.uploader = new CeloFileUploader(this.http, { queueLimit: this.limit });

    this.subscriptions.add(
      this.uploader.added$.subscribe((items) => {
        for (const item of items) {
          void this.onAfterAddingFile(item);
        }
      })
    );
    this.subscriptions.add(
      this.uploader.success$.subscribe((event) => this.onSuccessItem(event))
    );
    this.subscriptions.add(
      this.uploader.error$.subscribe((event) => this.onErrorItem(event))
    );
    this.subscriptions.add(
      this.uploader.complete$.subscribe(() =>
        this.onCompleteUpload(callback, data.conversationId)
      )
    );
  }

  isPhoto(fileItem: UploadItem) {
    if (
      fileItem.file &&
      fileItem.file.type &&
      fileItem.file.type.startsWith("image/")
    ) {
      return true;
    }
    return false;
  }

  isVideo(fileItem: UploadItem) {
    return this.supportedVideoFormats.includes(fileItem.file?.type ?? "");
  }

  videoStateFor(item: UploadItem): VideoUploadState | undefined {
    return this.videoStates.get(item.id);
  }

  get isConvertingVideo(): boolean {
    return this.videoConversions.size > 0;
  }

  /** PDFs go to the patient files endpoint with their own metadata payload. */
  isDocument(fileItem: UploadItem): boolean {
    return fileItem.file?.type?.startsWith("application/pdf") ?? false;
  }

  public onCompleteUpload(callback, targetConversationId?) {
    if (this.uploader.queue.filter((f) => f.isError).length === 0) {
      this.uploader.clear();
      callback({
        success: true,
        uploadedFiles: this.uploadedFiles,
        conversationId: targetConversationId,
      });
    }
  }

  private isValidFormat(file: File, validTypes: string[]) {
    const isValid = validTypes.some((t) => file.type.indexOf(t) !== -1);
    return isValid;
  }

  public async onAfterAddingFile(item: UploadItem) {
    // for some reason, pics which are saved by browser(right click -> save image)
    // wont upload. Renaming it works
    if (this.uploader.queue.length > config.max_file_upload_limit) {
      this.alertService.showSnackBar(
        "Import limit reached. You can only import 20 files at a time.",
        4
      );
      // remove the exceeding file
      this.uploader.remove(item);
    }
    const newname = "" + item.file.name;
    item.filename = newname.substr(0, newname.lastIndexOf("."));
    // Preserve the legacy "celo_" wire filename without mutating the original File.
    item.file = renameFile(item.file, "celo_" + newname);
    this.fileSelected();
    let validTypes = [];
    let fileType = "";
    fileType = item.file.type;

    if (this.type === "Photos") {
      validTypes = validTypes.concat(this.supportedImageFormats);
      validTypes = validTypes.concat(this.supportedVideoFormats);
    } else if (this.type === "Documents") {
      validTypes.push(this.supportedDocumentFormats);
    } else if (this.type === "Multi") {
      validTypes.push(this.supportedDocumentFormats);
      validTypes = validTypes.concat(this.supportedImageFormats);
      validTypes = validTypes.concat(this.supportedVideoFormats);
    }

    const isValidFormat = this.isValidFormat(item.file, validTypes);
    if (!isValidFormat) {
      this.uploader.remove(item);
      let errorMessage =
        "At this time we do not support direct upload of some of the files in your selection.";

      if (this.isValidFormat(item.file, this.supportedDocumentFormats)) {
        errorMessage +=
          " Please upload your PDF documents in Secure Library → Documents.";
      } else if (
        this.isValidFormat(item.file, this.supportedImageFormats) ||
        this.isValidFormat(item.file, this.supportedVideoFormats)
      ) {
        errorMessage +=
          " Please upload your photos and videos in Secure Library → Media.";
      }

      this.alertService.alert("Unsupported File", errorMessage);
      return;
    }

    if (this.isVideo(item)) {
      const conversion = this.convertVideo(item);
      this.videoConversions.set(item.id, conversion);
      void conversion.finally(() => this.videoConversions.delete(item.id));
      return;
    }

    let maxSize = 1920;
    if (this.sharedService.getImageQuality()) {
      maxSize = 4096;
    }

    if (fileType.startsWith("image/")) {
      const options: IResizeImageOptions = {
        file: item.file,
        maxSize,
        minSize: 1,
      };

      resizeImage(options).then(
        (resized: Blob) => {
          item.file = new File([resized], item.file.name, {
            type: resized.type,
          });
        },
        () => {
          this.uploader.remove(item);
          let errorMessage = `All photos must be at least ${options.minSize}x${options.minSize} pixels.`;
          errorMessage +=
            this.uploader.queue.length <= 0
              ? ""
              : " Unsupported photos are removed from the selection.";
          this.alertService.alert("Unsupported photos", errorMessage);
          return;
        }
      );
    }
  }

  /** Converts a queued video to the recording spec and keeps its thumbnail. */
  private async convertVideo(item: UploadItem) {
    const state: VideoUploadState = { isConverting: true };
    this.videoStates.set(item.id, state);
    try {
      const video = await this.prepareVideo(item.file, {
        onThumbnail: (thumbnail) => {
          state.thumbnail = thumbnail;
          state.thumbnailUrl = URL.createObjectURL(thumbnail);
        },
      });
      // Keep the "celo_" prefix added in onAfterAddingFile.
      item.file = new File([video.file], replaceExtension(item.file.name, "mp4"), {
        type: video.file.type,
      });
      state.thumbnail = video.thumbnail;
      state.videoUrl = URL.createObjectURL(video.file);
      state.isConverting = false;
    } catch (error) {
      this.removeVideoState(item.id);
      this.uploader.remove(item);
      this.fileSelected();
      const message =
        error instanceof VideoTooLongError ||
        error instanceof VideoTooLargeError
          ? error.message
          : "This video could not be prepared for upload.";
      this.alertService.alert("Unsupported video", message);
    }
  }

  private removeVideoState(id: string) {
    const state = this.videoStates.get(id);
    if (state?.thumbnailUrl) URL.revokeObjectURL(state.thumbnailUrl);
    if (state?.videoUrl) URL.revokeObjectURL(state.videoUrl);
    this.videoStates.delete(id);
  }

  private clearVideoStates() {
    for (const id of [...this.videoStates.keys()]) {
      this.removeVideoState(id);
    }
    this.videoConversions.clear();
  }

  private onSuccessItem({ item, response }: UploadSuccess) {
    let responseObj: any = response;
    if (!responseObj?.[0]) {
      responseObj = [responseObj];
    }
    for (const uploaded of responseObj) {
      let type = "";
      if (this.isDocument(item)) {
        type = "Document";
      } else if (item.file.type.startsWith("image/")) {
        type = "Photo";
      } else if (this.isVideo(item)) {
        type = "Video";
      }
      if (!this.uploadedFiles.map((file) => file.id).includes(uploaded.id)) {
        this.uploadedFiles.push({
          id: uploaded.id,
          type,
          fileName: uploaded.fileName,
          fileDescription:
            type == "Document"
              ? uploaded.fileDescription
              : uploaded.description,
        });
      }
    }
  }

  removeDuplicates() {
    const foundFiles = {};
    const duplicates: UploadItem[] = [];
    for (const fileItem of this.uploader.queue) {
      const name = fileItem.file.name;
      const size = fileItem.file.size;
      if (foundFiles[name] && foundFiles[name] === size) {
        duplicates.push(fileItem);
      }
      foundFiles[name] = size;
    }
    for (const fileItem of duplicates) {
      this.uploader.remove(fileItem);
    }
  }

  fileSelected() {
    this.removeDuplicates();
    this.uploaderQueueItems = this.uploader.queue.map((fileItem) => ({
      fileItem,
      description: "",
    }));
  }

  private onErrorItem({ response }: UploadFailure) {
    const resp: any = response;
    let message = "Error uploading file";
    message = resp?.uid && resp.uid[0] ? resp.uid[0] : message;
    if (!this.errored) {
      this.errored = true;
      setTimeout(() => {
        this.alertService.alert("Error", message, true);
        this.errored = false;
      }, 2000);
    }
  }

  public async upload() {
    this.uploadedFiles = [];

    if (this.videoConversions.size > 0) {
      await Promise.all(this.videoConversions.values());
    }

    if (this.uploader.isUploading) {
      return;
    }
    if (this.uploader.queue.length === 0) {
      return;
    }
    const request = this.buildUploadRequest();
    const failedItems = this.uploader.queue.filter((i) => i.isError);
    this.uploader.upload(
      request,
      failedItems.length > 0 ? failedItems : this.uploader.queue
    );
  }

  private buildUploadRequest(): UploadRequest {
    const base: any = {
      patientData: this.patient,
      isImported: true,
      consentId: undefined,
    };
    if (this.type === "Photos" || this.type === "Multi") {
      base.files = this.uploader.queue.map((item) => {
        if (this.isDocument(item)) {
          return {
            IsImported: true,
            fileName: "fileName",
            fileNameAlias: "fileNameAlias",
            fileDescription: "fileDescription",
          };
        }
        return undefined;
      });
    }
    return {
      url: (item) => this.resolveUploadUrl(item),
      method: "POST",
      headers: {
        CeloAuthorization: environment.celoAuth,
        "celo-region": this.authService.getDataRegion(),
        AppVersion: environment.appVersion,
        Authorization: `Bearer ${this.authService.getAccessToken()}`,
      },
      buildBody: (item) => this.buildUploadBody(item, base),
    };
  }

  private resolveUploadUrl(item: UploadItem): string {
    if (this.isDocument(item)) {
      return `${environment.celoApiEndpoint}/api/patientfiles/`;
    }
    return `${environment.celoApiEndpoint}/api/v2/media`;
  }

  private buildUploadBody(item: UploadItem, base: any): FormData {
    const form = new FormData();
    form.append("file", item.file, item.file.name);
    if (this.isDocument(item)) {
      // The PDF endpoint binds a single data value with these property names.
      form.append(
        "data",
        JSON.stringify({
          fileNameAlias: item.filename,
          fileDescription: item.description,
          isImported: true,
          patientData: this.patient,
        })
      );
      return form;
    }

    const data: any = {
      ...base,
      description: this.sharedService.isValidInput(item.description)
        ? item.description
        : "",
      filename: item.filename,
    };
    // The API requires a thumbnail and the media type for every video.
    const thumbnail = this.videoStateFor(item)?.thumbnail;
    if (this.isVideo(item) && thumbnail) {
      form.append("thumbnail", thumbnail, thumbnail.name);
      data.mediaType = "Video";
      data.media = { fileName: item.file.name, mimeType: item.file.type };
    }
    form.append("data", JSON.stringify(data));
    return form;
  }
}

interface IResizeImageOptions {
  maxSize: number;
  file: File;
  minSize: number;
}

function renameFile(file: File, name: string): File {
  return new File([file], name, { type: file.type });
}

function replaceExtension(fileName: string, extension: string): string {
  const dot = fileName.lastIndexOf(".");
  const baseName = dot > 0 ? fileName.substring(0, dot) : fileName;
  return `${baseName}.${extension}`;
}

const resizeImage = (settings: IResizeImageOptions): Promise<Blob> => {
  const file = settings.file;
  const maxSize = settings.maxSize;
  const reader = new FileReader();
  const image = new Image();
  const canvas = document.createElement("canvas");
  const dataURItoBlob = (dataURI: string) => {
    const bytes =
      dataURI.split(",")[0].indexOf("base64") >= 0
        ? atob(dataURI.split(",")[1])
        : (<any>window).unescape(dataURI.split(",")[1]);
    const mime = dataURI.split(",")[0].split(":")[1].split(";")[0];
    const max = bytes.length;
    const ia = new Uint8Array(max);
    for (let i = 0; i < max; i++) {
      ia[i] = bytes.charCodeAt(i);
    }
    return new Blob([ia], { type: mime });
  };
  const resize = () => {
    let width = image.width;
    let height = image.height;
    if (width < settings.minSize || height < settings.minSize) {
      return;
    }

    if (width > height) {
      if (width > maxSize) {
        height *= maxSize / width;
        width = maxSize;
      }
    } else {
      if (height > maxSize) {
        width *= maxSize / height;
        height = maxSize;
      }
    }

    canvas.width = width;
    canvas.height = height;
    canvas.getContext("2d").drawImage(image, 0, 0, width, height);
    const dataUrl = canvas.toDataURL("image/jpeg");
    return dataURItoBlob(dataUrl);
  };

  return new Promise((ok, no) => {
    if (!file.type.match(/image.*/)) {
      no(new Error("Not an image"));
      return;
    }

    reader.onload = (readerEvent: any) => {
      image.onload = () => {
        const x = resize();
        if (x) {
          ok(x);
        } else {
          no("Image too small");
        }
      };
      image.src = readerEvent.target.result;
    };
    reader.readAsDataURL(file);
  });
};
